export type BuildLevelName = 'Common' | 'Uncommon' | 'Rare' | 'Epic' | 'Legendary';

export type VehicleCertificationStatus =
  | 'unverified'
  | 'pending_review'
  | 'certified'
  | 'rejected'
  | 'revoked';

export interface BuildLevel {
  name: BuildLevelName;
  /** Wrench pips (1–5) derived from modification count tiers. */
  wrenches: number;
  /** Tailwind gradient stops for rim / glow washes. */
  color: string;
  glow: string;
  /** Adult collectible frame — metal weight, not cartoon foil. */
  frameClass: string;
  /** Optional specular foil layer class (Epic / Legendary / Rare hover). */
  foilClass: string;
  /** Show micro tier label under title. */
  showTierLabel: boolean;
}

/** Build score tiers from modification count (unchanged backend logic). */
export function getBuildLevel(modCount: number): BuildLevel {
  if (modCount >= 20) {
    return {
      name: 'Legendary',
      wrenches: 5,
      color: 'from-amber-300 via-orange-400 to-orange-600',
      glow: 'shadow-orange-500/40',
      frameClass: 'vehicle-card-frame vehicle-card-frame--legendary',
      foilClass: 'vehicle-card-foil vehicle-card-foil--legendary',
      showTierLabel: true,
    };
  }
  if (modCount >= 15) {
    return {
      name: 'Epic',
      wrenches: 4,
      color: 'from-orange-400 via-amber-500 to-orange-700',
      glow: 'shadow-orange-500/35',
      frameClass: 'vehicle-card-frame vehicle-card-frame--epic',
      foilClass: 'vehicle-card-foil vehicle-card-foil--epic',
      showTierLabel: true,
    };
  }
  if (modCount >= 10) {
    return {
      name: 'Rare',
      wrenches: 3,
      color: 'from-slate-300 via-cyan-400 to-slate-500',
      glow: 'shadow-cyan-500/25',
      frameClass: 'vehicle-card-frame vehicle-card-frame--rare',
      foilClass: 'vehicle-card-foil vehicle-card-foil--rare',
      showTierLabel: true,
    };
  }
  if (modCount >= 5) {
    return {
      name: 'Uncommon',
      wrenches: 2,
      color: 'from-emerald-500 via-emerald-400 to-teal-700',
      glow: 'shadow-emerald-500/20',
      frameClass: 'vehicle-card-frame vehicle-card-frame--uncommon',
      foilClass: '',
      showTierLabel: false,
    };
  }
  return {
    name: 'Common',
    wrenches: 1,
    color: 'from-gray-500 via-gray-600 to-gray-700',
    glow: 'shadow-gray-500/20',
    frameClass: 'vehicle-card-frame vehicle-card-frame--common',
    foilClass: '',
    showTierLabel: false,
  };
}

export function getBuildLevelLabel(modCount: number): string {
  const { wrenches, name } = getBuildLevel(modCount);
  const wrenchWord = wrenches === 1 ? 'wrench' : 'wrenches';
  return `Build score: ${wrenches} ${wrenchWord} (${name})`;
}

export function isVehicleCertified(
  status?: VehicleCertificationStatus | string | null
): boolean {
  return status === 'certified';
}
